import type { OptionChainRow } from '../services/store'

/**
 * Normalizes an incoming option chain array by reusing object references 
 * from the previous chain if the underlying data has not changed.
 * This prevents unnecessary React re-renders for rows where metrics (OI, Vol, LTP)
 * are identical between 2-second polling ticks.
 */
export function applyChainDiff(
    prevChain: OptionChainRow[] | undefined,
    nextChain: OptionChainRow[]
): OptionChainRow[] {
    if (!prevChain || prevChain.length === 0) return nextChain
    if (!nextChain || nextChain.length === 0) return prevChain

    // Fast check: if lengths differ or bounds differ drastically, just return new array
    if (prevChain.length !== nextChain.length) return nextChain

    let hasChanges = false
    const mergedChain = new Array<OptionChainRow>(nextChain.length)

    for (let i = 0; i < nextChain.length; i++) {
        const prev = prevChain[i]
        const next = nextChain[i]
        
        if (!next) continue

        if (!prev || prev.strike !== next.strike) {
            mergedChain[i] = next
            hasChanges = true
            continue
        }

        // Deep scalar comparison for OptionChainRow
        const isIdentical = 
            prev.ce.ltp === next.ce.ltp &&
            prev.ce.oi === next.ce.oi &&
            prev.ce.oiChange === next.ce.oiChange &&
            prev.ce.vol === next.ce.vol &&
            prev.ce.change === next.ce.change &&
            prev.ce.changePercent === next.ce.changePercent &&
            prev.ce.iv === next.ce.iv &&
            prev.pe.ltp === next.pe.ltp &&
            prev.pe.oi === next.pe.oi &&
            prev.pe.oiChange === next.pe.oiChange &&
            prev.pe.vol === next.pe.vol &&
            prev.pe.change === next.pe.change &&
            prev.pe.changePercent === next.pe.changePercent &&
            prev.pe.iv === next.pe.iv

        if (isIdentical) {
            // Reuse old reference to prevent row re-rendering
            mergedChain[i] = prev
        } else {
            mergedChain[i] = next
            hasChanges = true
        }
    }

    return hasChanges ? mergedChain : prevChain
}
